import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { User } from '../types'
import TaskForm from './TaskForm'

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

interface Task {
  id: number
  title: string
  description: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'suggested' | 'pending_approval' | 'approved' | 'assigned' | 'in_progress' | 'completed' | 'verified' | 'cancelled' | 'on_hold' | 'needs_revision'
  created_by: number
  created_by_name: string
  assigned_to: number | null
  assigned_to_name: string | null
  site_id: number | null
  site_name: string | null
  area_id: number | null
  area_name: string | null
  location_coords: [number, number] | null
  estimated_hours: number | null
  actual_hours: number | null
  due_date: string | null
  completed_at: string | null
  verified_at: string | null
  comment_count: number
  created_at: string
  updated_at: string
}

interface TaskComment {
  id: number
  task_id: number
  user_id: number
  user_name: string
  comment: string
  created_at: string
}

interface TaskHistory {
  id: number
  action: string
  old_value: string | null
  new_value: string | null
  field_name: string | null
  comment: string | null
  created_at: string
  user_name: string
  username: string
}

interface TaskDetailProps {
  taskId: number
  user: User
  onClose: () => void
  onUpdate: () => void
}

function TaskDetail({ taskId, user, onClose, onUpdate }: TaskDetailProps) {
  const [task, setTask] = useState<Task | null>(null)
  const [comments, setComments] = useState<TaskComment[]>([])
  const [history, setHistory] = useState<TaskHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'history' | 'location'>('comments')
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    fetchTaskDetails()
    fetchHistory()
  }, [taskId])

  const fetchTaskDetails = async () => {
    try {
      setLoading(true)
      const [taskResponse, commentsResponse] = await Promise.all([
        fetchWithAuth(`/api/tasks/${taskId}`),
        fetchWithAuth(`/api/tasks/${taskId}/comments`)
      ])

      if (!taskResponse.ok) {
        throw new Error('Failed to fetch task details')
      }

      const taskData = await taskResponse.json()
      setTask(taskData)

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        setComments(commentsData)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task details')
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async () => {
    try {
      const response = await fetchWithAuth(`/api/tasks/${taskId}/history`)
      if (response.ok) {
        const historyData = await response.json()
        setHistory(historyData)
      }
    } catch (err) {
      console.error('Failed to fetch task history:', err)
    }
  }

  const updateTaskStatus = async (newStatus: string) => {
    try {
      const response = await fetchWithAuth(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (!response.ok) {
        throw new Error('Failed to update task')
      }

      await fetchTaskDetails()
      await fetchHistory()
      onUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task')
    }
  }

  const addComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim()) return

    try {
      setSubmittingComment(true)
      const response = await fetchWithAuth(`/api/tasks/${taskId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment: newComment.trim() })
      })

      if (!response.ok) {
        throw new Error('Failed to add comment')
      }

      setNewComment('')
      await fetchTaskDetails()
      await fetchHistory()
      onUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment')
    } finally {
      setSubmittingComment(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#e74c3c'
      case 'high': return '#f39c12'
      case 'medium': return '#3498db'
      case 'low': return '#95a5a6'
      default: return '#95a5a6'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#27ae60'
      case 'verified': return '#2ecc71'
      case 'in_progress': return '#3498db'
      case 'assigned': return '#9b59b6'
      case 'approved': return '#1abc9c'
      case 'pending_approval': return '#f39c12'
      case 'suggested': return '#95a5a6'
      case 'cancelled': return '#e74c3c'
      case 'on_hold': return '#e67e22'
      case 'needs_revision': return '#e74c3c'
      default: return '#95a5a6'
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }

  const formatHistoryAction = (entry: TaskHistory) => {
    const { action, field_name, old_value, new_value } = entry
    
    switch (action) {
      case 'created':
        return `Created task with initial status: ${new_value}`
      case 'changed_status':
        return `Changed status from "${old_value}" to "${new_value}"`
      case 'changed_priority':
        return `Changed priority from "${old_value}" to "${new_value}"`
      case 'changed_assigned_to':
        return `${old_value ? `Reassigned from user ${old_value} to user ${new_value}` : `Assigned to user ${new_value}`}`
      case 'changed_title':
        return `Changed title from "${old_value}" to "${new_value}"`
      case 'changed_description':
        return `Updated description`
      case 'changed_due_date':
        return `Changed due date from "${old_value || 'none'}" to "${new_value || 'none'}"`
      case 'changed_estimated_hours':
        return `Changed estimated hours from "${old_value || 'none'}" to "${new_value || 'none'}"`
      case 'changed_location_coords':
        return `Updated GPS location`
      default:
        return field_name ? `Changed ${field_name} from "${old_value || 'none'}" to "${new_value || 'none'}"` : action
    }
  }

  if (loading) {
    return (
      <div className="task-detail-overlay">
        <div className="task-detail-container">
          <div className="loading">Loading task details...</div>
        </div>
      </div>
    )
  }

  if (!task) {
    return (
      <div className="task-detail-overlay">
        <div className="task-detail-container">
          <div className="error">Task not found</div>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    )
  }

  return (
    <div className="task-detail-overlay">
      <div className="task-detail-container">
        <div className="task-detail-header">
          <h3>📋 Task Details</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="task-detail-content">
          <div className="task-detail-main">
            <div className="task-info">
              <h4>{task.title}</h4>
              
              <div className="task-meta">
                <div className="meta-item">
                  <span className="meta-label">Priority:</span>
                  <span 
                    className="priority-badge" 
                    style={{ backgroundColor: getPriorityColor(task.priority) }}
                  >
                    {task.priority}
                  </span>
                </div>
                
                <div className="meta-item">
                  <span className="meta-label">Status:</span>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    {task.status.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="meta-item">
                  <span className="meta-label">Created by:</span>
                  <span>{task.created_by_name}</span>
                </div>
                
                <div className="meta-item">
                  <span className="meta-label">Assigned to:</span>
                  <span>{task.assigned_to_name || 'Unassigned'}</span>
                </div>
                
                {task.site_name && (
                  <div className="meta-item">
                    <span className="meta-label">Location:</span>
                    <span>
                      🏠 {task.site_name}
                      {task.area_name && <> → 📍 {task.area_name}</>}
                    </span>
                  </div>
                )}
                
                <div className="meta-item">
                  <span className="meta-label">Created:</span>
                  <span>{formatDate(task.created_at)}</span>
                </div>
                
                {task.due_date && (
                  <div className="meta-item">
                    <span className="meta-label">Due:</span>
                    <span>{formatDate(task.due_date)}</span>
                  </div>
                )}
                
                {task.estimated_hours && (
                  <div className="meta-item">
                    <span className="meta-label">Estimated:</span>
                    <span>{task.estimated_hours}h</span>
                  </div>
                )}
              </div>

              {task.description && (
                <div className="task-description">
                  <h5>Description</h5>
                  <p>{task.description}</p>
                </div>
              )}

              <div className="task-actions">
                {task.assigned_to === user.id && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => setIsEditing(true)}
                  >
                    ✏️ Edit Task
                  </button>
                )}
                {task.status === 'assigned' && task.assigned_to === user.id && (
                  <button 
                    className="btn btn-success"
                    onClick={() => updateTaskStatus('in_progress')}
                  >
                    Start Task
                  </button>
                )}
                {task.status === 'in_progress' && task.assigned_to === user.id && (
                  <button 
                    className="btn btn-success"
                    onClick={() => updateTaskStatus('completed')}
                  >
                    Mark Complete
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="task-tabs">
            <div className="tab-header">
              <button 
                className={`tab-button ${activeTab === 'comments' ? 'active' : ''}`}
                onClick={() => setActiveTab('comments')}
              >
                💬 Comments ({comments.length})
              </button>
              <button 
                className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                📜 History ({history.length})
              </button>
              {task.location_coords && (
                <button 
                  className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
                  onClick={() => setActiveTab('location')}
                >
                  📍 Location
                </button>
              )}
            </div>
            
            <div className="tab-content">
              {activeTab === 'comments' && (
                <div className="task-comments">
                  <form onSubmit={addComment} className="comment-form">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      rows={3}
                    />
                    <button 
                      type="submit" 
                      className="btn btn-sm btn-success"
                      disabled={submittingComment || !newComment.trim()}
                    >
                      {submittingComment ? 'Adding...' : 'Add Comment'}
                    </button>
                  </form>

                  <div className="comments-list">
                    {comments.map(comment => (
                      <div key={comment.id} className="comment-item">
                        <div className="comment-header">
                          <strong>{comment.user_name}</strong>
                          <span className="comment-date">{formatDate(comment.created_at)}</span>
                        </div>
                        <div className="comment-text">{comment.comment}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {activeTab === 'history' && (
                <div className="task-history">
                  <div className="history-list">
                    {history.map(entry => (
                      <div key={entry.id} className="history-item">
                        <div className="history-header">
                          <strong>{entry.user_name || entry.username}</strong>
                          <span className="history-date">{formatDate(entry.created_at)}</span>
                        </div>
                        <div className="history-action">
                          {formatHistoryAction(entry)}
                        </div>
                        {entry.comment && (
                          <div className="history-comment">{entry.comment}</div>
                        )}
                      </div>
                    ))}
                    {history.length === 0 && (
                      <div className="no-history">No history available for this task.</div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'location' && task.location_coords && (
                <div className="task-location">
                  <div className="location-info">
                    <p>
                      <strong>📍 GPS Coordinates:</strong><br />
                      Latitude: {task.location_coords[0].toFixed(6)}<br />
                      Longitude: {task.location_coords[1].toFixed(6)}
                    </p>
                  </div>
                  <div className="location-map">
                    <MapContainer
                      center={task.location_coords}
                      zoom={15}
                      style={{ height: '400px', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker position={task.location_coords} />
                    </MapContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <TaskForm
          user={user}
          isEditing={true}
          taskId={taskId}
          initialData={task}
          onSubmit={() => {
            setIsEditing(false)
            fetchTaskDetails()
            fetchHistory()
            onUpdate()
          }}
          onCancel={() => setIsEditing(false)}
        />
      )}
    </div>
  )
}

export default TaskDetail